import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import { toast } from 'react-toastify';
import Grid from '@mui/material/Grid2';
import { Box, Button, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import { PrimaryGrid } from '@ui/components/common/Grid';
import { Icon } from '@iconify/react';
import { useForm } from 'react-hook-form';
import { FileErrorType } from '@application/enums/file-error-type';
import { useTranslation } from 'react-i18next';
import FileUploaderController from '@ui/components/FileUploader/FileUploaderController';
import { Delete, Download } from 'react-iconly';
import { PdfIcon } from '@ui/assets/custom-icons/pdf-icon';
import { getFileIcon } from '@application/utilities/fileUploaderHelper';
import { ExcelIcon } from '@ui/assets/custom-icons/excel-icon';
import { useFileUploadOptional } from '@ui/contexts/FileUploadContext';
import ConfirmationDialog from '@ui/components/common/Dialog/ConfirmationDialog';
import CustomDialog from '@ui/components/common/Dialog';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { createEventAttachmentAsync, getAllEventAttachmentsAsync, deleteEventAttachmentAsync, getDocumentDownloadUrlAsync } from '@application/features/vdkEventDocument/vdkEventDocumentThunk';
import { useEffect } from 'react';
import dayjs from 'dayjs';
import { ATTACHMENT_DATE_DISPLAY } from '@application/utilities/date-formats';
import { WordIcon } from '@ui/assets/custom-icons/word-icon';
import { EventDocumentType } from '@application/enums/event-document-type';
import { hasPermission } from '@application/helpers/permission-helper';
import { Add } from 'iconsax-react';
const AttachmentsTab = ({ isFamilyFest = false }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const { eventId } = useParams();
    const { status, eventAttachments, eventAttachmentsTotalCount } = useAppSelector((state) => state.vdkEventDocument);
    const currentUserRoles = useAppSelector((state) => state.auth.currentUser?.role);
    const collaboratorAccessLevel = useAppSelector((state) => state.auth.currentUser?.CollaboratorAccessLevel);
    const isPreviewMode = useAppSelector((state) => state.auth.isPreviewMode);
    const isCollaborator = Array.isArray(currentUserRoles) && (currentUserRoles.includes('Collaborator') || currentUserRoles.includes('TransportManager'));
    const isSfeermeester = Array.isArray(currentUserRoles) && currentUserRoles.includes('Sfeermeester');
    const isExternal = Number(collaboratorAccessLevel) === 1;
    const loggedInUserPermissions = useAppSelector((state) => state.auth.currentUser?.permissions || []);
    const canUpdateEvents = hasPermission(loggedInUserPermissions, 'Permissions.VdkEvents.Update');
    const [downloadingIds, setDownloadingIds] = useState([]);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [deleteTargetRow, setDeleteTargetRow] = useState(null);
    const [fileUploadDialogOpen, setFileUploadDialogOpen] = useState(false);
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const contextValue = useFileUploadOptional();
    const { control } = useForm({
        defaultValues: {
            myFiles: []
        }
    });
    const [localRows, setLocalRows] = useState([]);
    const [localFileError, setLocalFileError] = useState({
        hasError: false,
        errorType: FileErrorType.NoError
    });
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    const [sortModel, setSortModel] = useState([]);
    // Grid column fields don't always match the backend's sort keys.
    const ATTACHMENT_SORT_FIELD_MAP = {
        fileName: 'filename',
        addedOn: 'addedon'
    };
    const fetchEventAttachments = () => {
        if (!eventId) {
            return;
        }
        const sort = sortModel[0];
        dispatch(getAllEventAttachmentsAsync({
            eventId,
            pageNumber: paginationModel.page + 1,
            pageSize: paginationModel.pageSize,
            sortBy: sort ? (ATTACHMENT_SORT_FIELD_MAP[sort.field] ?? sort.field.toLowerCase()) : undefined,
            sortOrder: sort?.sort ?? 'desc'
        }));
    };
    useEffect(() => {
        fetchEventAttachments();
    }, [dispatch, eventId, paginationModel.page, paginationModel.pageSize, sortModel]);
    // Transform API attachments to match local format
    const apiRows = eventAttachments?.map((attachment) => ({
        id: attachment.id,
        title: attachment.title || 'Attachment',
        fileName: attachment.fileName || 'Unknown file',
        icon: getFileIcon({ name: attachment.fileName || '' }),
        addedOn: attachment.addedOnDate
            ? dayjs(attachment.addedOnDate).format(ATTACHMENT_DATE_DISPLAY)
            : dayjs(new Date()).format(ATTACHMENT_DATE_DISPLAY),
        url: attachment.url,
        type: attachment.type
    })) || [];
    // TODO(pagination+preview-mode conflict): This client-side filter runs AFTER server-side
    // pagination, on only the current page of results. With `rowCount`/`paginationMode='server'`
    // now driven by the *unfiltered* page's `eventAttachmentsTotalCount`, a page of `pageSize`
    // items can be filtered down to fewer visible rows here, so the footer's reported total/page
    // count will not match what the user actually sees in preview mode. Needs a human decision:
    // either disable this client-side role filtering while paginated, or move the type-visibility
    // filtering server-side (e.g. an additional `documentType`/role query param on
    // GetAllVDKEventAttachmentQuery) so pagination and filtering stay in sync.
    // In preview mode for Family Fest, filter docs to only those visible to the previewed role
    const visibleApiRows = isPreviewMode && isFamilyFest
        ? apiRows.filter((row) => {
            if (isSfeermeester)
                return row.type === EventDocumentType.SpheresMaster || row.type === EventDocumentType.All;
            if (isCollaborator && isExternal)
                return row.type === EventDocumentType.External || row.type === EventDocumentType.All;
            if (isCollaborator)
                return row.type === EventDocumentType.Internal || row.type === EventDocumentType.All;
            return true;
        })
        : apiRows;
    const rows = visibleApiRows.length > 0 ? visibleApiRows : localRows;
    const removeAttachment = contextValue?.removeAttachment;
    const handleFilesSubmitted = async (files, title, type) => {
        if (!eventId) {
            return;
        }
        try {
            for (const file of files) {
                await dispatch(createEventAttachmentAsync({
                    eventId: eventId,
                    fileTitle: title,
                    type: type,
                    file
                })).unwrap();
            }
            toast.success(t('events.eventDetails.attachmentsTabs.attachmentAddedSuccessfully'));
        }
        catch (error) {
            toast.error(error?.error || t('events.eventDetails.attachmentsTabs.failedToAddAttachment'));
        }
        finally {
            fetchEventAttachments();
        }
    };
    const handleDownloadClick = async (row) => {
        setDownloadingIds((prev) => [...prev, row.id]);
        try {
            // Get the download URL from the API
            const downloadUrl = await dispatch(getDocumentDownloadUrlAsync(row.id.toString())).unwrap();
            if (downloadUrl) {
                const urlWithDownload = downloadUrl.includes('?')
                    ? `${downloadUrl}&download=true`
                    : `${downloadUrl}?download=true`;
                const link = document.createElement('a');
                link.href = urlWithDownload;
                link.setAttribute('download', row.fileName);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                toast.success(t('events.eventDetails.attachmentsTabs.downloadSuccessful'));
            }
        }
        catch (error) {
            // silent
        }
        finally {
            setDownloadingIds((prev) => prev.filter((id) => id !== row.id));
        }
    };
    const handleDeleteClick = (row) => {
        setDeleteTargetRow(row);
        setConfirmDialogOpen(true);
    };
    const handleConfirmDelete = async () => {
        if (deleteTargetRow && eventId) {
            try {
                await dispatch(deleteEventAttachmentAsync({
                    id: deleteTargetRow.id.toString()
                })).unwrap();
                toast.success(t('events.eventDetails.attachmentsTabs.attachmentDeletedSuccessfully'));
                fetchEventAttachments();
            }
            catch (error) {
                toast.error(error?.error || t('events.eventDetails.attachmentsTabs.failedToDeleteAttachment'));
            }
        }
        setConfirmDialogOpen(false);
        setDeleteTargetRow(null);
    };
    const columns = [
        {
            field: 'title',
            headerName: t('events.eventDetails.attachmentsTabs.title'),
            flex: 1,
            minWidth: 200,
            filterable: false,
            renderCell: (params) => (_jsxs(Box, { display: 'flex', alignItems: 'center', gap: 1, py: 1.7, children: [params.row.icon === 'custom:pdf-icon' ? (_jsx(PdfIcon, {})) : params.row.icon === 'custom:excel-icon' ? (_jsx(ExcelIcon, { color: '#22E122' })) : params.row.icon === 'custom:word-icon' ? (_jsx(WordIcon, {})) : (_jsx(Icon, { icon: params.row.icon, fontSize: 24 })), _jsx(Typography, { variant: 'body2', children: params.row.title })] }))
        },
        {
            field: 'fileName',
            headerName: t('events.eventDetails.attachmentsTabs.fileName'),
            flex: 1,
            minWidth: 200,
            filterable: false
        },
        {
            field: 'addedOn',
            headerName: t('events.eventDetails.attachmentsTabs.addedOn'),
            flex: 1,
            minWidth: 200,
            filterable: false
        },
        {
            field: 'actions',
            headerName: t('events.eventDetails.attachmentsTabs.actions'),
            width: 150,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                const isDownloading = downloadingIds.includes(params.row.id);
                return (_jsxs(Box, { display: 'flex', py: 1, gap: 1, children: [!isCollaborator && (_jsx(IconButton, { color: 'inherit', size: 'small', onClick: () => handleDeleteClick(params.row), children: _jsx(Delete, {}) })), _jsx(IconButton, { color: 'inherit', size: 'small', onClick: () => handleDownloadClick(params.row), disabled: isDownloading, children: _jsx(Download, {}) })] }));
            }
        }
    ];
    return (_jsxs(Box, { children: [isSmallScreen && (_jsx(FileUploaderController, { name: 'myFiles', control: control, fileError: contextValue?.fileError || localFileError, setFileError: contextValue?.setFileError || setLocalFileError, fileUploaderProps: {
                    allowedFileTypes: [
                        'pdf',
                        'doc',
                        'docx',
                        'xls',
                        'xlsx',
                        'csv',
                        'jpg',
                        'jpeg',
                        'png',
                        'gif',
                        'bmp',
                        'webp',
                        'mp4'
                    ],
                    onFilesSubmitted: handleFilesSubmitted,
                    allowedFileTypesText: 'PDF, Word, Excel, Images, MP4',
                    allowedFileSize: 20,
                    allowedTotalFileSize: 50,
                    infoText: t('fileUpload.infoText'),
                    disabled: isCollaborator || !canUpdateEvents,
                    isFamilyFest
                } })), _jsx(Grid, { container: true, spacing: 2, pr: 0.5, children: _jsx(Grid, { size: { xs: 12, lg: 12 }, children: _jsx(PrimaryGrid, { noRowsText: t('events.eventDetails.attachmentsTabs.noRowsText'), rows: rows, columns: columns, showHeaderControls: false, showNewHeaderControls: isSmallScreen ? false : true, customButton: !isSmallScreen && (_jsx(Box, { display: 'flex', justifyContent: 'flex-end', pb: '12px', children: _jsx(Button, { variant: 'contained', color: 'primary', size: 'medium', startIcon: _jsx(Add, {}), onClick: () => setFileUploadDialogOpen(true), disabled: isCollaborator || !canUpdateEvents, children: t('fileUpload.addAttachments') }) })), sx: {
                            height: { xs: 'calc(100dvh - 420px)' }
                        }, boxProps: { sx: { backgroundColor: 'transparent' } }, paginationMode: 'server', sortingMode: 'server', paginationModel: paginationModel, onPaginationModelChange: setPaginationModel, rowCount: eventAttachmentsTotalCount ?? 0, sortModel: sortModel, onSortModelChange: setSortModel }) }) }), _jsx(CustomDialog, { backgroundVariant: 'white', open: fileUploadDialogOpen, onClose: () => setFileUploadDialogOpen(false), 
                // title={t('fileUpload.addAttachments')}
                maxWidth: 'md', children: _jsx(Box, { sx: { p: 3 }, children: _jsx(FileUploaderController, { name: 'myFiles', control: control, fileError: contextValue?.fileError || localFileError, setFileError: contextValue?.setFileError || setLocalFileError, fileUploaderProps: {
                            allowedFileTypes: [
                                'pdf',
                                'doc',
                                'docx',
                                'xls',
                                'xlsx',
                                'csv',
                                'jpg',
                                'jpeg',
                                'png',
                                'gif',
                                'bmp',
                                'webp',
                                'mp4'
                            ],
                            onFilesSubmitted: (files, title, type) => {
                                handleFilesSubmitted(files, title, type);
                                setFileUploadDialogOpen(false);
                            },
                            allowedFileTypesText: 'PDF, Word, Excel, Images, MP4',
                            allowedFileSize: 20,
                            allowedTotalFileSize: 50,
                            infoText: t('fileUpload.infoText'),
                            disabled: isCollaborator || !canUpdateEvents,
                            isFamilyFest
                        } }) }) }), _jsx(ConfirmationDialog, { open: confirmDialogOpen, onClose: () => {
                    setConfirmDialogOpen(false);
                    setDeleteTargetRow(null);
                }, title: t('events.eventDetails.attachmentsTabs.confirmDeleteTitle'), actions: _jsxs(_Fragment, { children: [_jsx(Button, { fullWidth: true, variant: 'outlined', onClick: () => {
                                setConfirmDialogOpen(false);
                                setDeleteTargetRow(null);
                            }, children: t('actions.cancel') }), _jsx(Button, { fullWidth: true, onClick: handleConfirmDelete, variant: 'contained', color: 'primary', children: t('actions.deleteNow') })] }), children: _jsx(Box, { padding: 3, children: _jsx(Typography, { variant: 'body1', children: t('events.eventDetails.attachmentsTabs.confirmDeleteText', {
                            fileName: deleteTargetRow?.title
                        }) }) }) })] }));
};
export default AttachmentsTab;
